'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

type ThemeMode = 'dark' | 'light' | 'system';

const ACCENT_COLORS: Record<string, string> = {
  violet: '239 84% 67%',
  blue: '217 91% 60%',
  emerald: '160 84% 39%',
  rose: '347 77% 50%',
  amber: '38 92% 50%',
  cyan: '189 94% 43%',
};

interface ThemeContextValue {
  themeMode: ThemeMode;
  accentColor: string;
  setThemeMode: (mode: ThemeMode) => void;
  setAccentColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  themeMode: 'dark',
  accentColor: 'violet',
  setThemeMode: () => {},
  setAccentColor: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('dark');
  const [accentColor, setAccentColorState] = useState('violet');

  const applyTheme = useCallback((mode: ThemeMode) => {
    const root = document.documentElement;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const isDark = mode === 'dark' || (mode === 'system' && prefersDark);

    if (isDark) {
      root.classList.remove('light');
      root.classList.add('dark');
      // Dark theme CSS variables
      root.style.setProperty('--background', '240 10% 4%');
      root.style.setProperty('--foreground', '240 5% 96%');
      root.style.setProperty('--card', '240 6% 10%');
      root.style.setProperty('--card-foreground', '240 5% 96%');
      root.style.setProperty('--border', '240 4% 16%');
      root.style.setProperty('--input', '240 4% 14%');
      root.style.setProperty('--muted', '240 4% 20%');
      root.style.setProperty('--muted-foreground', '240 5% 55%');
      root.style.setProperty('--accent', '240 4% 16%');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
      // Light theme CSS variables
      root.style.setProperty('--background', '0 0% 98%');
      root.style.setProperty('--foreground', '240 10% 10%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '240 10% 10%');
      root.style.setProperty('--border', '240 5% 84%');
      root.style.setProperty('--input', '240 5% 90%');
      root.style.setProperty('--muted', '240 5% 92%');
      root.style.setProperty('--muted-foreground', '240 5% 45%');
      root.style.setProperty('--accent', '240 5% 90%');
    }
  }, []);

  const applyAccent = useCallback((color: string) => {
    const hsl = ACCENT_COLORS[color] || ACCENT_COLORS.violet;
    document.documentElement.style.setProperty('--primary', hsl);
    document.documentElement.style.setProperty('--ring', hsl);
  }, []);

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedTheme = (localStorage.getItem('missionlog_theme') as ThemeMode) || 'dark';
    const savedAccent = localStorage.getItem('missionlog_accent') || 'violet';
    setThemeModeState(savedTheme);
    setAccentColorState(savedAccent);
    applyTheme(savedTheme);
    applyAccent(savedAccent);

    // Listen for system preference changes when mode is 'system'
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemChange = () => {
      const currentMode = (localStorage.getItem('missionlog_theme') as ThemeMode) || 'dark';
      if (currentMode === 'system') {
        applyTheme('system');
      }
    };
    mediaQuery.addEventListener('change', handleSystemChange);
    return () => mediaQuery.removeEventListener('change', handleSystemChange);
  }, [applyTheme, applyAccent]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('missionlog_theme', mode);
    applyTheme(mode);
  }, [applyTheme]);

  const setAccentColor = useCallback((color: string) => {
    setAccentColorState(color);
    localStorage.setItem('missionlog_accent', color);
    applyAccent(color);
  }, [applyAccent]);

  return (
    <ThemeContext.Provider value={{ themeMode, accentColor, setThemeMode, setAccentColor }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
